import { useFormContext, useWatch } from 'react-hook-form'

import {
  getValidatedRecipients,
  TransferFormValues,
} from '~/components/forms/transfer/transferSchema'
import { useAddCurrentFormToBatch } from '~/hooks/useAddCurrentFormToBatch'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { useCanAddToBatch } from '~/hooks/useTransactionBatch'
import { OrgSmartAccount } from '~/types'
import { Address } from '~/utils/address'
import { buildSwapTransferBatchMetadata } from '~/utils/smartAccounts/batch'
import { getSwapTransferTransactionTypes } from '~/utils/smartAccounts/transactions'

// Consolidates the "Add to batch" handler shared by ReviewDialog and
// DialogFormContent. The offramp flag is passed through verbatim;
// the server derives the authoritative offramp semantics from the BankTransfer
// metadata payload, so we don't try to detect it client-side.
export const useAddTransferToBatch = ({
  senderAccount,
  title,
  setIsOpen,
  onSuccess,
  isOfframp = false,
  isPrivate,
}: {
  senderAccount: OrgSmartAccount | undefined
  title: string
  setIsOpen: (open: boolean) => void
  onSuccess?: () => void
  isOfframp?: boolean
  isPrivate?: boolean
}) => {
  const methods = useFormContext<TransferFormValues>()
  const { addCurrentFormToBatch, isProcessing } = useAddCurrentFormToBatch({
    onSuccess,
  })

  const { control } = methods
  const calls = useWatch({ control, name: 'calls' })
  const recipients = useWatch({ control, name: 'recipients' })
  const transferToken = useWatch({ control, name: 'transferToken' })
  const swapData = useWatch({ control, name: 'swapData' })
  const earnToken = useWatch({ control, name: 'swapData.earnToken' })

  const transferTokenMetadata = useTokenMetadata({
    address: transferToken?.token,
    chainId: transferToken?.chainId,
  })
  const sellTokenMetadata = useTokenMetadata({
    address: swapData?.sellToken?.token,
    chainId: swapData?.sellToken?.chainId,
  })

  const transactionChainId =
    swapData?.sellToken?.chainId ?? transferToken?.chainId
  const isBridging =
    !!swapData?.sellToken?.chainId &&
    !!transferToken?.chainId &&
    swapData.sellToken.chainId !== transferToken.chainId

  const transactionTypes = getSwapTransferTransactionTypes({
    swapData,
    isBridging,
    earnToken,
  })

  // Gate the button on token metadata being resolved — a defaulted 18 would
  // silently misscale 6-decimal tokens like USDC. The sell token is optional
  // (transfer-only flows have no sell side), so only require it when present.
  const isMetadataReady =
    !!transferTokenMetadata?.validToken &&
    (!swapData?.sellToken || !!sellTokenMetadata?.validToken)

  const canAddToBatch = useCanAddToBatch({
    smartAccountId: senderAccount?.id,
    chainId: transactionChainId,
  })

  const handleAddToBatch = async () => {
    if (
      !senderAccount ||
      !transferToken?.token ||
      !transferToken.amount ||
      !transactionChainId ||
      !calls?.length ||
      !isMetadataReady
    ) {
      return
    }

    const validatedRecipients = getValidatedRecipients(recipients ?? []).map(
      (r) => ({
        address: r.receiver.address,
        amount: r.amount,
        compliantPaymentRelationId: r.receiver.compliantPaymentRelationId,
      }),
    )

    await addCurrentFormToBatch({
      smartAccountId: senderAccount.id,
      chainId: transactionChainId,
      title,
      types: transactionTypes,
      calls,
      metadata: buildSwapTransferBatchMetadata({
        recipients: validatedRecipients,
        transferToken: {
          token: transferToken.token as Address.Type,
          amount: transferToken.amount,
          chainId: transferToken.chainId,
        },
        transferTokenDecimals: transferTokenMetadata.decimals,
        sellToken: swapData?.sellToken
          ? {
              token: swapData.sellToken.token as Address.Type,
              amount: swapData.sellToken.amount,
              chainId: swapData.sellToken.chainId,
            }
          : undefined,
        sellTokenDecimals: sellTokenMetadata?.decimals,
        transactionChainId,
        transactionTypes,
        providerTransactionId: swapData?.providerTransactionId,
        swapRouter: swapData?.swapRouter,
        settlementRouter: swapData?.settlementRouter,
        swapSlippage: swapData?.slippage,
        earnToken: earnToken
          ? { ...earnToken, token: earnToken.token as Address.Type }
          : undefined,
        isOfframp,
        isPrivate,
      }),
      memo: methods.getValues('memo'),
      setIsOpen,
    })
  }

  return {
    handleAddToBatch,
    isProcessing,
    isMetadataReady,
    transactionChainId,
    transactionTypes,
    isBridging,
    canAddToBatch,
  }
}
